import { useNavigate, useParams } from "react-router-dom";
import * as Yup from "yup";
import { useFormik } from "formik";
import Typography from "@mui/material/Typography";
import { Box, Button, FormHelperText, Grid, Skeleton } from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { Quill } from "react-quill";
import FormikControl from "../../../../components/common/FormikControl";
import { BackNavIcon } from "../../../../../assets/common-svg/CommonSvg";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { notify } from "../../../../../bmr/utils/methods/notification";
import {
  createEmailTemplate,
  fetchEmailTemplateById,
  fetchEmailTypes,
  isExistTypeId,
  updateEmailTemplate,
  fetchWhatsAppTemplate,
} from "../../../../slices/emailTemplates";
import { useDispatch, useSelector } from "react-redux";
import BsModal from "../../../../components/common/BaseModal";
import FilePreviewModal from "../../../../components/common/FilePreview";
import ChannelCard from "./ChannelCard";
import { selectAllCombinedRoles } from "../../../../slices/roles";

Quill.register(Quill.import("attributors/style/align"), true);
Quill.register(Quill.import("attributors/style/background"), true);
Quill.register(Quill.import("attributors/style/color"), true);
Quill.register(Quill.import("attributors/style/font"), true);
Quill.register(Quill.import("attributors/style/size"), true);

const CHANNEL_CONFIG = [
  {
    key: "in_app",
    label: "In App",
    messageField: "in_app_message",
    roleField: "inapp_role",
    apiRoleKey: "inapp_role_id",
  },
  {
    key: "email",
    label: "Email",
    messageField: "communication_message",
    subjectField: "subject",
    roleField: "communication_role",
    apiRoleKey: "role_id",
  },
  // {//@info not to send on pre-prod
  //   key: "sms",
  //   label: "SMS",
  //   messageField: "sms_message",
  //   roleField: "sms_role",
  //   apiRoleKey: "sms_role_id",
  // },
  {
    key: "whatsapp",
    label: "WhatsApp",
    messageField: "whatsapp_message",
    roleField: "whatsapp_role",
    apiRoleKey: "whatsapp_role_id",
  },
];

const CHANNEL_ROLES_KEY_MAP = {
  in_app: "IN-APP",
  email: "EMAIL",
  sms: "SMS",
  whatsapp: "WHATSAPP",
};

const CHANNEL_ALL_FIELDS_MAP = {
  in_app: {
    in_app_message: "",
    inapp_role: [],
    other_in_app_emails: "",
  },
  email: {
    communication_message: "",
    subject: "",
    communication_role: [],
    other_emails: "",
    files: null,
  },
  sms: {
    sms_message: "",
    sms_role: [],
    other_sms_numbers: "",
  },
  whatsapp: {
    whatsapp_message: "",
    whatsapp_template_name: "",
    whatsapp_template_variables: [],
    whatsapp_role: [],
    other_whatsapp_numbers: "",
    whatsapp_template_raw: "",
  },
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^\d{12}$/;

const validateCommaSeparated = (value, regex) => {
  if (!value) return true;
  const items = value.split(",").map((v) => v.trim());
  return items.every((item) => regex.test(item));
};

const CHANNEL_ROLE_FIELD_MAP = {
  in_app: "inapp_role",
  email: "communication_role",
  sms: "sms_role",
  whatsapp: "whatsapp_role",
};

const CHANNEL_OTHER_FIELD_MAP = {
  in_app: "other_in_app_emails",
  email: "other_emails",
  sms: "other_sms_numbers",
  whatsapp: "other_whatsapp_numbers",
};

const RECIPIENT_REQUIRED_MESSAGE =
  "Either Role Permission or the email/number field is required";

const buildRecipientRequiredTest = (otherFieldKey, roleFieldKey, isEnabled) =>
  function () {
    if (!isEnabled) return true;

    const otherVal = this.parent?.[otherFieldKey];
    const roleVal = this.parent?.[roleFieldKey];

    const hasOther = Boolean(otherVal && String(otherVal).trim());
    const hasRole = Array.isArray(roleVal) && roleVal.length > 0;

    return hasOther || hasRole;
  };

const buildValidationSchema = (channelsEnabled) => {
  const recipientShape = {};

  Object.keys(CHANNEL_ROLE_FIELD_MAP).forEach((channelKey) => {
    const roleFieldKey = CHANNEL_ROLE_FIELD_MAP[channelKey];
    const otherFieldKey = CHANNEL_OTHER_FIELD_MAP[channelKey];
    const isEnabled = Boolean(channelsEnabled[channelKey]);
    const isPhoneChannel = channelKey === "sms" || channelKey === "whatsapp";
    const formatRegex = isPhoneChannel ? phoneRegex : emailRegex;
    const formatMessage = isPhoneChannel
      ? "Enter valid 12-digit number(s) with country code"
      : "Enter valid email(s)";

    recipientShape[roleFieldKey] = Yup.array().test(
      "recipient-required",
      RECIPIENT_REQUIRED_MESSAGE,
      buildRecipientRequiredTest(otherFieldKey, roleFieldKey, isEnabled),
    );

    recipientShape[otherFieldKey] = Yup.string()
      .test("valid-format", formatMessage, (value) =>
        validateCommaSeparated(value, formatRegex),
      )
      .test(
        "recipient-required",
        RECIPIENT_REQUIRED_MESSAGE,
        buildRecipientRequiredTest(otherFieldKey, roleFieldKey, isEnabled),
      );
  });

  return Yup.object().shape({
    name: Yup.string().required("Template name is required"),
    communication_type_id: Yup.string().required("Type is required"),
    status: Yup.number().required("Status is required"),

    subject: channelsEnabled.email
      ? Yup.string().required("Subject is required")
      : Yup.string().nullable(),
    communication_message: channelsEnabled.email
      ? Yup.string().required("Email message is required")
      : Yup.string().nullable(),
    sms_message: channelsEnabled.sms
      ? Yup.string().required("SMS message is required")
      : Yup.string().nullable(),
    whatsapp_message: channelsEnabled.whatsapp
      ? Yup.string().required("WhatsApp message is required")
      : Yup.string().nullable(),
    in_app_message: channelsEnabled.in_app
      ? Yup.string().required("In App message is required")
      : Yup.string().nullable(),
    whatsapp_template_name: channelsEnabled.whatsapp
      ? Yup.string().required("WhatsApp template is required")
      : Yup.string().nullable(),

    ...recipientShape,

    files: Yup.mixed().test(
      "fileSize",
      "File size must be less than 10MB",
      (value) => {
        if (!value) return true;
        if (value instanceof File) return value.size <= 10 * 1024 * 1024;
        return true;
      },
    ),
  });
};

const parseRolesByChannel = (raw) => {
  if (!raw) return {};
  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
};

const parseWhatsAppData = (raw) => {
  if (!raw) return { template: "", variables: [] };

  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
    const map = parsed.map || {};

    const variables = Object.keys(map)
      .sort(
        (a, b) => Number(a.replace(/\D/g, "")) - Number(b.replace(/\D/g, "")),
      )
      .map((key) => map[key]);

    return {
      template: parsed.template || "",
      variables,
    };
  } catch {
    return { template: "", variables: [] };
  }
};

const parseSampleData = (raw) => {
  if (!raw) return { EMAIL: "", SMS: "", WHATSAPP: "", "IN-APP": "" };

  try {
    const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;

    return {
      EMAIL: (parsed.EMAIL || []).join(","),
      SMS: (parsed.SMS || []).join(","),
      WHATSAPP: (parsed.WHATSAPP || []).join(","),
      "IN-APP": (parsed["IN-APP"] || []).join(","),
    };
  } catch {
    return { EMAIL: "", SMS: "", WHATSAPP: "" };
  }
};

const buildInitialValues = (response = null) => {
  const rbc = parseRolesByChannel(response?.roles_by_channel);
  const toArr = (rbcKey, legacyVal) =>
    rbc[rbcKey] ?? (legacyVal != null ? [legacyVal] : []);
  const waData = parseWhatsAppData(response?.whatsapp_message);
  const sample = parseSampleData(response?.sample_data);

  let initialStatus = response?.status;
  if (initialStatus === "Active") {
    initialStatus = 1;
  } else if (initialStatus === "In-Active") {
    initialStatus = 0;
  } else if (initialStatus !== undefined && initialStatus !== null) {
    initialStatus = Number(initialStatus);
  } else {
    initialStatus = 1;
  }

  return {
    status: initialStatus,
    name: response?.name ?? "",
    subject: response?.subject ?? "",
    files: response?.media ?? null,
    communication_type_id: response?.communication_type_id ?? "",
    communication_message: response?.message ?? "",
    sms_message: response?.sms_message ?? "",

    whatsapp_template_name: waData.template,
    whatsapp_template_variables: waData.variables,
    whatsapp_message: "", // preview only

    in_app_message: response?.in_app_message ?? "",

    communication_role: toArr("EMAIL", response?.communication_role),
    sms_role: toArr("SMS", response?.sms_role),
    whatsapp_role: toArr("WHATSAPP", response?.whatsapp_role),
    inapp_role: toArr("IN-APP", response?.inapp_role),

    whatsapp_template_raw: "",

    other_emails: sample.EMAIL ?? "",
    other_sms_numbers: sample.SMS ?? "",
    other_whatsapp_numbers: sample.WHATSAPP ?? "",
    other_in_app_emails: sample["IN-APP"] ?? "",
  };
};

// ─── Channel enabled state factory ───────────────────────────────────────────
const buildChannelState = (response = null) =>
  response
    ? {
        in_app: Number(response.in_app_enabled) === 1,
        email: Number(response.email_enabled) === 1,
        sms: Number(response.sms_enabled) === 1,
        whatsapp: Number(response.whatsapp_enabled) === 1,
      }
    : { in_app: false, email: true, sms: false, whatsapp: false };

// ─── Top form field definitions ───────────────────────────────────────────────
const buildFormOptions = (typeOptions) => [
  {
    label: "Template Status",
    type: "select",
    name: "status",
    required: true,
    options: [
      { value: 1, label: "Active" },
      { value: 0, label: "In-Active" },
    ],
  },
  {
    label: "Template Name",
    name: "name",
    placeholder: "Enter Template Name",
    required: true,
  },
  {
    label: "Type",
    type: "select",
    name: "communication_type_id",
    placeholder: "Select Type",
    options: typeOptions,
    required: true,
  },
];

// ─── Component ────────────────────────────────────────────────────────────────
const CreateEmailTemplate = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isEditMode = Boolean(id);
  const fileInputRef = useRef(null);
  const allRoles = useSelector(selectAllCombinedRoles);

  const quillRefs = useRef({});
  const preservedContent = useRef({
    in_app_message: "",
    inapp_role: [],
    other_in_app_emails: "",
    communication_message: "",
    subject: "",
    communication_role: [],
    other_emails: "",
    files: null,
    sms_message: "",
    sms_role: [],
    other_sms_numbers: "",
    whatsapp_message: "",
    whatsapp_template_name: "",
    whatsapp_template_variables: [],
    whatsapp_role: [],
    other_whatsapp_numbers: "",
    whatsapp_template_raw: "",
  });

  const [typeOptions, setTypeOptions] = useState([]);
  const [emailTypes, setEmailTypes] = useState([]);
  const [whatsappTemplateOptions, setWhatsappTemplateOptions] = useState([]);
  const [assignedVariables, setAssignedVariables] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [channelsEnabled, setChannelsEnabled] = useState(buildChannelState());
  const [initialChannelsEnabled, setInitialChannelsEnabled] = useState(
    buildChannelState(),
  );
  const [activeEditor, setActiveEditor] = useState("email");
  const [pendingFormData, setPendingFormData] = useState(null);
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewFileUrl, setPreviewFileUrl] = useState("");
  const [isMediaDeleted, setIsMediaDeleted] = useState(false);
  const [initialValues, setInitialValues] = useState(buildInitialValues());
  const validationSchema = useMemo(
    () => buildValidationSchema(channelsEnabled),
    [channelsEnabled],
  );

  const buildWhatsAppMapping = (variables = []) => {
    const map = {};
    variables.forEach((val, index) => {
      if (val) {
        map[`{{${index + 1}}}`] = val;
      }
    });
    return map;
  };

  const buildSampleData = (values) => {
    const parse = (str) =>
      str
        ? str
            .split(",")
            .map((v) => v.trim())
            .filter(Boolean)
        : [];

    return {
      EMAIL: parse(values.other_emails),
      SMS: parse(values.other_sms_numbers),
      WHATSAPP: parse(values.other_whatsapp_numbers),
      "IN-APP": parse(values.other_in_app_emails),
    };
  };
  const formOptions = useMemo(
    () => buildFormOptions(typeOptions),
    [typeOptions],
  );
  const buildRolesByChannel = useCallback((values) => {
    const result = {};
    CHANNEL_CONFIG.forEach(({ key, roleField }) => {
      const val = values[roleField];
      result[CHANNEL_ROLES_KEY_MAP[key]] = Array.isArray(val)
        ? val
        : val
        ? [val]
        : [];
    });
    return result;
  }, []);
  const buildFormData = useCallback(
    (values) => {
      const fd = new FormData();
      fd.append("name", values.name);
      fd.append("communication_type_id", values.communication_type_id);
      fd.append("status", values.status);

      CHANNEL_CONFIG.forEach(
        ({ key, messageField, subjectField, roleField, apiRoleKey }) => {
          const enabledKey =
            key === "in_app" ? "in_app_enabled" : `${key}_enabled`;
          fd.append(enabledKey, channelsEnabled[key] ? 1 : 0);

          const rolesArr = Array.isArray(values[roleField])
            ? values[roleField]
            : [];
          if (rolesArr.length > 0) fd.append(apiRoleKey, rolesArr[0]);

          if (subjectField) fd.append("subject", values[subjectField] || "");

          if (key === "whatsapp") {
            const templateId = values.whatsapp_template_name;
            const variables = values.whatsapp_template_variables || [];
            const mapping = buildWhatsAppMapping(variables);

            const whatsappPayload = {
              template: templateId || "",
              map: mapping,
            };

            fd.append("whatsapp_message", JSON.stringify(whatsappPayload));
          } else {
            const apiKey = key === "email" ? "message" : messageField;
            fd.append(apiKey, values[messageField] || "");
          }
        },
      );

      fd.append(
        "roles_by_channel",
        JSON.stringify(buildRolesByChannel(values)),
      );
      const sampleData = buildSampleData(values);
      fd.append("sample_data", JSON.stringify(sampleData));
      if (isMediaDeleted) {
        fd.append("is_media", -1);
      }
      if (values.files instanceof File) fd.append("files", values.files);
      return fd;
    },
    [channelsEnabled, buildRolesByChannel, isMediaDeleted],
  );

  const formik = useFormik({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit: async (values) => {
      try {
        setLoading(true);
        const formData = buildFormData(values);
        if (isEditMode) {
          await dispatch(updateEmailTemplate({ id, data: formData })).unwrap();
          navigate("/bmr/notification-center");
        } else {
          const result = await dispatch(
            isExistTypeId({ id: values.communication_type_id }),
          ).unwrap();
          if (result.templateExists) {
            setPendingFormData(formData);
            setDuplicateModalOpen(true);
          } else {
            await dispatch(createEmailTemplate(formData)).unwrap();
            navigate("/bmr/notification-center");
          }
        }
      } catch (err) {
        notify.error(err || err?.message || "Something Went Wrong !!");
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    formik.validateForm();
  }, [channelsEnabled, initialValues]);

  const isSystemType = useMemo(() => {
    const selected = emailTypes.find(
      (item) => String(item.id) === String(formik.values.communication_type_id),
    );

    const name = selected?.name?.toLowerCase();

    return (
      name === "sign up" || name === "signup" || name === "forgot password"
    );
  }, [formik.values.communication_type_id, emailTypes]);

  // ── Fetch email types on mount ──
  useEffect(() => {
    dispatch(fetchEmailTypes())
      .unwrap()
      .then((res) => {
        setEmailTypes(res);
        setTypeOptions(
          res.map((item) => ({ label: item.name, value: item.id })),
        );
      });
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchWhatsAppTemplate())
      .unwrap()
      .then((res) => {
        const mapped = res.map((item) => ({
          label: item.elementName,
          value: item.id,
          data: item.data,
        }));

        setWhatsappTemplateOptions(mapped);
      });
  }, [dispatch]);

  useEffect(() => {
    if (
      !formik.values.whatsapp_template_name ||
      !whatsappTemplateOptions.length
    )
      return;

    const selectedTemplate = whatsappTemplateOptions.find(
      (t) => t.value === formik.values.whatsapp_template_name,
    );

    if (!selectedTemplate) return;

    // set raw template
    formik.setFieldValue("whatsapp_template_raw", selectedTemplate.data);

    // generate preview
    const formatted = formatWhatsAppMessage(
      selectedTemplate.data,
      formik.values.whatsapp_template_variables,
    );

    formik.setFieldValue("whatsapp_message", formatted);
  }, [formik.values.whatsapp_template_name, whatsappTemplateOptions]);

  useEffect(() => {
    if (isSystemType) {
      formik.setFieldValue("communication_role", []);
      formik.setFieldValue("sms_role", []);
      formik.setFieldValue("whatsapp_role", []);
      formik.setFieldValue("inapp_role", []);
    }
  }, [isSystemType]);
  // ── Fetch template data in edit mode ──
  useEffect(() => {
    if (!id) return;
    (async () => {
      try {
        setIsDataLoading(true);
        const response = await dispatch(fetchEmailTemplateById(id)).unwrap();
        const initVals = buildInitialValues(response);
        setInitialValues(initVals);
        preservedContent.current = { ...initVals };
        const channelState = buildChannelState(response);
        setChannelsEnabled(channelState);
        setInitialChannelsEnabled(channelState);
      } catch (err) {
        console.error(err);
      } finally {
        setIsDataLoading(false);
      }
    })();
  }, [id, dispatch]);

  useEffect(() => {
    if (!formik.values.communication_type_id) {
      setAssignedVariables([]);
      return;
    }
    const selected = emailTypes.find(
      (item) => String(item.id) === String(formik.values.communication_type_id),
    );
    setAssignedVariables(selected?.available_variables || []);
  }, [formik.values.communication_type_id, emailTypes]);

  useEffect(() => {
    if (!whatsappTemplateOptions) return;
    setAssignedVariables((prev) => [
      ...prev,
      ...whatsappTemplateOptions.flatMap(
        (opt) => opt.data?.available_variables || [],
      ),
    ]);
  }, [whatsappTemplateOptions]);

  const handleToggleChannel = useCallback(
    (channelKey) => {
      const isEnabled = channelsEnabled[channelKey];
      const fieldsConfig = CHANNEL_ALL_FIELDS_MAP[channelKey] || {};
      const fieldKeys = Object.keys(fieldsConfig);

      // Clear touched/validation state for the channel's fields
      fieldKeys.forEach((f) => {
        formik.setFieldTouched(f, false, false);
      });
      setChannelsEnabled((prev) => ({ ...prev, [channelKey]: !isEnabled }));
      setActiveEditor(channelKey);
    },
    [channelsEnabled, formik],
  );

  const formatWhatsAppMessage = (template, variables = []) => {
    if (!template) return "";

    let msg = template;

    msg = msg.replace(/\\n/g, "\n");

    msg = msg.replace(/{{(\d+)}}/g, (_, num) => {
      return variables[num - 1] || `{{${num}}}`;
    });

    return msg
      .split("\n\n")
      .map((p) => `<p>${p.trim()}</p>`)
      .join("");
  };

  const handleInsertVariable = useCallback(
    (variable, channelKey, slotIndex = null) => {
      if (channelKey === "whatsapp") {
        const current = formik.values.whatsapp_template_variables || [];
        const updated = [...current];

        const targetIndex =
          typeof slotIndex === "number" && slotIndex >= 0
            ? slotIndex
            : updated.findIndex((v) => !v);

        // stop if all slots are already filled
        if (targetIndex === -1) return;

        updated[targetIndex] = variable;

        formik.setFieldValue("whatsapp_template_variables", updated);

        const formatted = formatWhatsAppMessage(
          formik.values.whatsapp_template_raw,
          updated,
        );

        formik.setFieldValue("whatsapp_message", formatted);

        return;
      }

      const editor = quillRefs.current[channelKey || activeEditor]?.getEditor();

      if (!editor) return;

      const range = editor.getSelection(true);

      editor.insertText(range.index, variable);
      editor.setSelection(range.index + variable.length);
    },
    [formik, activeEditor],
  );

  const handleRemoveVariable = useCallback(
    (slotIndex) => {
      const current = formik.values.whatsapp_template_variables || [];
      if (
        !Array.isArray(current) ||
        slotIndex < 0 ||
        slotIndex >= current.length
      )
        return;

      const updated = [...current];
      updated[slotIndex] = "";

      formik.setFieldValue("whatsapp_template_variables", updated);

      const formatted = formatWhatsAppMessage(
        formik.values.whatsapp_template_raw,
        updated,
      );

      formik.setFieldValue("whatsapp_message", formatted);
    },
    [formik],
  );

  const handleConfirmDuplicate = async () => {
    try {
      await dispatch(createEmailTemplate(pendingFormData)).unwrap();
      setDuplicateModalOpen(false);
      navigate("/bmr/notification-center");
    } catch (err) {
      console.error(err);
    }
  };

  const channelsDirty = useMemo(
    () =>
      JSON.stringify(channelsEnabled) !==
      JSON.stringify(initialChannelsEnabled),
    [channelsEnabled, initialChannelsEnabled],
  );
  //@info Remove it when  submit disable is confirmed
  // const isSubmitDisabled =
  // loading || formik.isSubmitting || !formik.isValid || (!formik.dirty && !channelsDirty);

  //@info make submit disable till it add the value in variable.
  const whatsappVariables = formik.values.whatsapp_template_variables || [];

  const areWhatsAppVariablesFilled =
    !channelsEnabled.whatsapp ||
    whatsappVariables.every((item) => item && String(item).trim() !== "");

  const isAnyChannelEnabled = Object.values(channelsEnabled).some(Boolean);

  const isFormDirty = useMemo(() => {
    if (channelsDirty) return true;

    const keysToCompare = [
      "status",
      "name",
      "subject",
      "communication_type_id",
      "communication_message",
      "sms_message",
      "in_app_message",
      "whatsapp_template_name",
      "whatsapp_template_variables",
      "communication_role",
      "sms_role",
      "whatsapp_role",
      "inapp_role",
      "other_emails",
      "other_sms_numbers",
      "other_whatsapp_numbers",
      "other_in_app_emails",
      "files",
    ];

    return keysToCompare.some((key) => {
      const initial = initialValues[key];
      const current = formik.values[key];

      if (Array.isArray(initial) || Array.isArray(current)) {
        return JSON.stringify(initial || []) !== JSON.stringify(current || []);
      }

      const isQuillField = [
        "communication_message",
        "sms_message",
        "in_app_message",
      ].includes(key);
      if (isQuillField) {
        const cleanVal = (val) => {
          if (!val) return "";
          let cleaned = String(val).trim();
          cleaned = cleaned.replace(/^(<p><br><\/p>|<p><\/p>|<br>)+$/g, "");
          return cleaned;
        };
        return cleanVal(initial) !== cleanVal(current);
      }

      return String(initial ?? "") !== String(current ?? "");
    });
  }, [formik.values, initialValues, channelsDirty]);

  // In create mode, use formik.dirty (standard Formik dirty check).
  // In edit mode, use our custom isFormDirty that also accounts for channel toggling.
  const hasDirtyChanges = isEditMode ? isFormDirty : formik.dirty;

  const isSubmitDisabled =
    loading ||
    formik.isSubmitting ||
    !formik.isValid ||
    (isEditMode && !hasDirtyChanges) ||
    !areWhatsAppVariablesFilled ||
    !isAnyChannelEnabled;
  // ── File helpers ──

  const roleOptions = useMemo(
    () => allRoles.map((r) => ({ label: r.title, value: r.id })),
    [allRoles],
  );
  return (
    <div className="!p-6">
      {/* Back */}
      <button
        className="cursor-pointer flex items-center gap-2 mb-3"
        onClick={() => navigate("/bmr/notification-center")}
      >
        <BackNavIcon /> Back
      </button>

      <div className="!p-4 mt-1 bg-white flex flex-col gap-4 rounded-2xl">
        <Typography variant="h5" fontWeight={600}>
          {isEditMode ? "Update Notifications" : "Create New Notifications"}
        </Typography>

        {isDataLoading ? (
          <Skeleton variant="rectangular" width="100%" height={200} />
        ) : (
          <form onSubmit={formik.handleSubmit}>
            {/* ── Top fields ── */}
            <Grid container spacing={2} mb={3}>
              {formOptions.map((props) => (
                <Grid key={props.name} item xs={12}>
                  <FormikControl formik={formik} {...props} />
                </Grid>
              ))}
            </Grid>

            <Box mb={3}>
              <Typography variant="subtitle1" fontWeight={700} mb={2}>
                Communication Channels{" "}
                <span style={{ color: "#ef4444" }}>*</span>
              </Typography>

              {!isAnyChannelEnabled && (
                <FormHelperText error sx={{ mt: -1, mb: 2 }}>
                  At least one communication channel must be enabled.
                </FormHelperText>
              )}

              <Grid container spacing={2}>
                {CHANNEL_CONFIG.map((channel) => (
                  <Grid
                    item
                    xs={12}
                    md={6}
                    key={channel.key}
                    sx={{ display: "flex" }}
                  >
                    <ChannelCard
                      channelKey={channel.key}
                      label={channel.label}
                      enabled={channelsEnabled[channel.key]}
                      onToggle={() => handleToggleChannel(channel.key)}
                      messageField={channel.messageField}
                      subjectField={channel.subjectField}
                      roleField={channel.roleField}
                      formik={formik}
                      quillRef={(el) => (quillRefs.current[channel.key] = el)}
                      onEditorFocus={() => setActiveEditor(channel.key)}
                      assignedVariables={assignedVariables}
                      onVariableClick={handleInsertVariable}
                      onVariableRemove={handleRemoveVariable}
                      roleOptions={roleOptions}
                      disableRole={isSystemType}
                      whatsappTemplateOptions={whatsappTemplateOptions}
                      fileInputRef={fileInputRef}
                      previewOpen={previewOpen}
                      setPreviewOpen={setPreviewOpen}
                      previewFileUrl={previewFileUrl}
                      setPreviewFileUrl={setPreviewFileUrl}
                      isMediaDeleted={isMediaDeleted}
                      setIsMediaDeleted={setIsMediaDeleted}
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* ── Submit ── */}
            <LoadingButton
              loading={loading}
              type="submit"
              variant="contained"
              disabled={isSubmitDisabled}
            >
              {isEditMode ? "Update" : "Submit"}
            </LoadingButton>
          </form>
        )}
      </div>

      {duplicateModalOpen && (
        <BsModal
          open={duplicateModalOpen}
          onClose={() => setDuplicateModalOpen(false)}
          title="Template Already Exists"
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, p: 2 }}>
            <Typography>
              An email template for the selected type already exists.
            </Typography>
            <Typography>
              Creating a new template will automatically mark the existing one
              as <strong>Inactive</strong>.
            </Typography>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button variant="contained" onClick={handleConfirmDuplicate}>
                Save
              </Button>
            </Box>
          </Box>
        </BsModal>
      )}

      {previewOpen && (
        <FilePreviewModal
          fileURL={previewFileUrl}
          open={previewOpen}
          setOpen={setPreviewOpen}
          ModalTitle="File Preview"
        />
      )}
    </div>
  );
};

export default CreateEmailTemplate;
